import { createStyles, WithStyles, withStyles } from "@material-ui/core";
import classnames from "classnames";
import * as d3 from "d3";
import * as React from "react";

import { PiePieceData } from ".";
import { createArc, createExplodePiece } from "./utils";

const styles = createStyles({
    root: {},
    pie: {},
    center: {},
});

interface PieProps extends WithStyles<typeof styles> {
    data: PiePieceData[];
    innerRadius: number;
    offset?: number;
    outerRadius: number;
    onClick: (data: PiePieceData) => void;
    centerElement?: React.ReactNode;
    animate?: boolean;
    className?: string;
    colors?: d3.ScaleOrdinal<string, string>;
}

export const Pie = withStyles(styles)((props: PieProps) => {
    const ref = React.useRef(null);

    const cache = React.useRef(props.data);
    const {
        offset = 1,
        outerRadius,
        innerRadius,
        classes,
        className,
        onClick,
        data,
        animate,
        colors = d3.scaleOrdinal(d3.schemeTableau10),
    } = props;

    const colorFn = React.useCallback(
        (datum: { data: PiePieceData }, index: number) => {
            if (!!datum.data.color) return datum.data.color;
            return colors(index.toString());
        },
        [colors]
    );

    const attachAnimationListeners = React.useCallback(
        (
            selection: d3.Selection<any, { data: PiePieceData }, any, unknown>
        ) => {
            selection
                .on("mouseover", function (d) {
                    d3.select(`[data-label="${d.data.id}-dot"]`)
                        .transition()
                        .ease(d3.easeQuadOut)
                        .attr("r", 10);
                    d3.select(`[data-label="${d.data.id}-pie"]`)
                        .transition()
                        .ease(d3.easeQuadOut)
                        .attr(
                            "d",
                            createExplodePiece(innerRadius, outerRadius) as any
                        );
                })
                .on("mouseout", function (d) {
                    d3.select(`[data-label="${d.data.id}-dot"]`)
                        .transition()
                        .ease(d3.easeQuadOut)
                        .attr("r", 7);
                    d3.select(`[data-label="${d.data.id}-pie"]`)
                        .transition()
                        .ease(d3.easeQuadIn)
                        .attr(
                            "d",
                            createArc(innerRadius, outerRadius, offset) as any
                        );
                })
                .on("click", function (d) {
                    onClick?.(d.data);
                });
        },
        [innerRadius, outerRadius, offset, onClick]
    );

    React.useEffect(() => {
        const createPie = d3
            .pie<PiePieceData>()
            .value(d => d.value)
            .sort(null);

        const _data: any = createPie(data);
        const prevData = createPie(cache.current);

        const group = d3.select(ref.current);

        const groupWithData = group.selectAll("g.arc").data(_data);

        groupWithData.exit().remove();

        const groupWithUpdate = groupWithData
            .enter()
            .append("g")
            .attr("class", "arc");

        const path: any = groupWithUpdate
            .append("path")
            .merge(groupWithData.select("path.arc"))
            .attr("class", "arc")
            .attr("data-label", (d: any, i) => `${d.data.id}-pie`)
            .attr("fill", colorFn)
            .attr("data-cy", (d: any, i) => `${d.data.id}-pie`);
        const arcTween = (d, i) => {
            const interpolator = d3.interpolate(prevData[i], d);

            return t =>
                createArc(innerRadius, outerRadius, offset)(interpolator(t));
        };

        if (animate) {
            attachAnimationListeners(path);
            path.on("mouseover", function (this: any, d: any) {
                d3.select(`[data-label="${d.data.id}-dot"]`)
                    .transition()
                    .ease(d3.easeQuadOut)
                    .attr("r", 10);
                d3.select(this)
                    .transition()
                    .ease(d3.easeQuadOut)
                    .attr("d", createExplodePiece(innerRadius, outerRadius));
            })
                .on("mouseout", function (this: any, d: any) {
                    d3.select(this)
                        .transition()
                        .ease(d3.easeQuadIn)
                        .attr("d", createArc(innerRadius, outerRadius, offset));
                    d3.select(`[data-label="${d.data.id}-dot"]`)
                        .transition()
                        .ease(d3.easeQuadOut)
                        .attr("r", 7);
                })
                .on("click", function (d: any) {
                    onClick?.(d.data);
                })
                .transition()
                .attrTween("d", arcTween);
        } else {
            path.attr("d", createArc(innerRadius, outerRadius, offset) as any)
                .on("click", function (d) {
                    onClick?.(d.data);
                })
                .on("mouseover", function (this: any) {
                    d3.select(this).transition();
                })
                .on("mouseout", function (this: any) {
                    d3.select(this).transition();
                });
        }

        cache.current = _data;
        //// eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        data,
        animate,
        innerRadius,
        outerRadius,
        offset,
        onClick,
        colors,
        attachAnimationListeners,
        colorFn,
    ]);

    // NOTE: style is used here for two reasons:
    // 1. it is highly dynamic (width, height) and prefer this method over using props in the styles object above.
    // 2. to ensure that certain styling is not overridden by the containing component.
    return (
        <div
            className={classnames(classes.root, className)}
            style={{
                position: "relative",
                height: outerRadius * 2,
                width: outerRadius * 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <svg
                width={outerRadius * 2}
                height={outerRadius * 2}
                className={classes.pie}
                style={{ position: "absolute", top: 0, left: 0 }}
            >
                <g
                    ref={ref}
                    transform={`translate(${outerRadius} ${outerRadius})`}
                />
            </svg>
            <div
                className={classes.center}
                style={{
                    transform: `translate(${outerRadius} ${outerRadius})`,
                }}
            >
                {props.centerElement}
            </div>
        </div>
    );
});
